import React, { useEffect } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import GetSpace from "../../../utils/space"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { faClose, faFile, faFileSignature, faLayerGroup, faMoneyBill } from "@fortawesome/free-solid-svg-icons"
import InputField from "../../../components/InputField/InputField"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { useRegister } from "../../../hooks/Register/useRegister"
import { useError } from "../../../context/useErrorContext"
import { useFileImage } from "../../../hooks/useFileImage"
import { ImageLibraryPicker } from "../../../components/ImageLibrary/ImageLibraryPicker"
import { getImageLibraryFile } from "../../../utils/imageLibraryFile"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { AddtionalEntity } from "../../../Objects/Addtional/AddtionalEntity"
import { useGetAddtional } from "../../../hooks/Addtional/useGetAddtional"
import { useRemoveAddtional } from "../../../hooks/Addtional/useRemoveAddtional"
import { useSaveAddtional } from "../../../hooks/Addtional/useSaveAddtional"
import { useCreateAddtional } from "../../../hooks/Addtional/useCreateAddtional"
import { useTranslation } from "react-i18next"
import { Permissions } from "../../../Enum/PermissionsType"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { CanView } from "../../../components/CanView/CanView"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { AssociationsPopup } from "../../../components/Associations/AssociationsPopup"
import { comboMemory, drinksMemory, mealsMemory } from "../../../Objects/Memory/Memory"
import { resolveAdditionalAssociations } from "../../../components/Associations/associationResolvers"
import { useApplyAdditional } from "../../../hooks/Addtional/useApplyAdditional"
import { useGetMeals } from "../../../hooks/Meals/useGetMeals"
import { useGetDrinks } from "../../../hooks/Drinks/useGetDrinks"
import { useGetCombo } from "../../../hooks/Combo/useGetCombo"
import { AdditionalApplyPopup } from "./AdditionalApplyPopup"

interface AddtionalPupopProbs {
  title: string
  addtional?: AddtionalEntity
  onClonse: () => void
  onCopy?: () => void
}

export const AddtionalPupop: React.FC<AddtionalPupopProbs> = ({ addtional, title, onClonse, onCopy }) => {
  const { t } = useTranslation("addtional")

  const isCopiedDraft = addtional?.id === -1

  const [showAssociations, setShowAssociations] = React.useState(false)

  const [enableApplyPopup, setEnableApplyPopup] = React.useState(false)

  const [enableRemoveApplyPopup, setEnableRemoveApplyPopup] = React.useState(false)

  const canEdite = !usePermission(Permissions.CanEditeAddtionals)

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const { handleGetAddtional } = useGetAddtional()

  const { handleApplyAdditional, handleRemoveAdditionalApply } = useApplyAdditional()

  const { handleGetMeals } = useGetMeals()

  const { handleGetDrinks } = useGetDrinks()

  const { handleGetCombo } = useGetCombo()

  const { setNewConfirmation } = useConfirmation()

  const { handleRemove } = useRemoveAddtional()

  const { fileImage, setFileImage } = useFileImage()

  const [selectedImageIds, setSelectedImageIds] = React.useState<number[]>([])

  const [selectedImageUrls, setSelectedImageUrls] = React.useState<string[]>(
    addtional?.iconUrl ? [addtional.iconUrl] : [],
  )

  const { handleSaveAddtional } = useSaveAddtional()

  const { setError } = useError()

  const {
    name,
    description,
    status,
    value,
    handleNameChange,
    handleValueChange,
    handleDescriptionChange,
    handleStatusChange,
    handleSubmitForm,
    handleInitialValue,
  } = useCreateAddtional()

  useEffect(() => {
    handleInitialValue(addtional)
  }, [addtional])

  const handleTrySubmit = (event: React.FormEvent) => {
    event.preventDefault()

    if (Number(value) <= 0) {
      setError(t("general:label_invalid"), t("label_value_must_be_greater_than_zero"))

      return
    }

    const result = handleSubmitForm()

    if ("errors" in result) {
      const errorMessages = result.errors.map((e) => e.message).join("\n")

      setError(t("general:label_invalid"), errorMessages)

      return
    }

    handleSaveAddtional(
      result,
      () => {
        onClonse()
        handleGetAddtional(() => {})
      },
      fileImage,
    )
  }

  const handleCopyClick = async () => {
    setFileImage(null)

    onCopy?.()
  }

  const refreshProductAssociations = () => {
    handleGetAddtional(() => {})
    handleGetMeals(() => {})
    handleGetDrinks(() => {})
    handleGetCombo(() => {})
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faLayerGroup}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <>
                    {addtional && addtional.id !== -1 && (
                      <div style={{ width: "100px", marginRight: "5px" }}>
                        <GenericDropDown
                          data={Object.mapEnumToStrings(EEnableStatusType, {
                            exclude: [EEnableStatusType.All],
                          })}
                          initialState={status}
                          handlerFuncion={handleStatusChange}
                          disable={canEdite}
                          descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.select.select"
                        />

                        <GetSpace right={10} />
                      </div>
                    )}

                    <PopupHelpButton
                      helpKey="pages.products.addtional.addtionalpupop.1"
                      variant="global"
                      title={t("helpPopup:global.additional.title")}
                      description={t("helpPopup:global.additional.description")}
                      bullets={t("helpPopup:global.additional.bullets", { returnObjects: true }) as string[]}
                    />

                    <GetSpace right={5} />

                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faClose}
                      onClick={onClonse}
                      size="small"
                      descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.button.onClonse"
                    />
                  </>
                </div>
              }
            />

            {isCopiedDraft && <CopyDraftAlert />}

            <form onSubmit={handleTrySubmit}>
              <section className="p-2">
                <ImageLibraryPicker
                  disabled={canEdite}
                  imageProfile="additional"
                  maxSelection={1}
                  selectedIds={selectedImageIds}
                  selectedUrls={selectedImageUrls}
                  onChange={async (ids, urls) => {
                    setSelectedImageIds(ids)
                    setSelectedImageUrls(urls)

                    if (ids.length === 0) {
                      setFileImage(
                        urls.length === 0 && addtional?.iconUrl
                          ? new File(["remove-image"], "__remove_icon__", { type: "text/plain" })
                          : null,
                      )
                      return
                    }

                    setFileImage(await getImageLibraryFile(ids[0], urls[0]))
                  }}
                />

                <div className={defaultStyle.divisionBar}></div>

                <InputField
                  type="text"
                  value={name}
                  icon={faFileSignature}
                  maxLength={INPUT_SIZE.EXTRA_SERVICE.name}
                  placeholder={t("label_name_place_holder")}
                  onInvalidMessage={t("label_name_onInvalidMessage")}
                  onChange={handleNameChange}
                  required
                  disable={canEdite}
                  descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.input.namePlaceHolder"
                />

                <GetSpace top={10} />

                <InputField
                  maxLength={10}
                  type="number"
                  icon={faMoneyBill}
                  placeholder={t("label_value_place_holder")}
                  onInvalidMessage={t("label_value_onInvalidMessage")}
                  value={value.toString()}
                  onChange={handleValueChange}
                  required
                  disable={canEdite}
                  descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.input.valuePlaceHolder"
                />

                <GetSpace top={10} />

                <InputField
                  type="text"
                  icon={faFile}
                  maxLength={INPUT_SIZE.EXTRA_SERVICE.description}
                  placeholder={t("label_description_place_holder")}
                  onInvalidMessage={t("label_description_onInvalidMessage")}
                  value={description}
                  onChange={handleDescriptionChange}
                  disable={canEdite}
                  descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.input.descriptionPlaceHolder"
                />

                <GetSpace top={5} />
              </section>

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeAddtionals}>
                  <Button
                    label={t("buttons:button_save")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.button.save"
                  />
                </CanView>

                {addtional && addtional.id !== -1 && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      label={t("buttons:button_register")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRegisterColor}
                      onClick={handleEnableRegister}
                      descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.button.register"
                    />

                    <GetSpace right={5} />

                    <Button
                      label={t("buttons:button_view_associations")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGPlusColor}
                      onClick={() => setShowAssociations(true)}
                      descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.button.viewAssociations"
                    />

                    <CanView permission={Permissions.CanEditeAddtionals}>
                      {onCopy && (
                        <>
                          <GetSpace right={5} />
                          <Button
                            label={t("buttons:button_copy")}
                            typeButton="button"
                            type="primary"
                            size="medium"
                            bgColor={THEME.colors.buttonBGPlusColor}
                            onClick={handleCopyClick}
                            descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.button.copy"
                          />
                        </>
                      )}
                    </CanView>
                  </>
                )}

                <CanView permission={Permissions.CanEditeAddtionals}>
                  {addtional && addtional.id !== -1 && (
                    <>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_remove")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        iconColor={THEME.colors.buttonFGRemoveColor}
                        bgColor={THEME.colors.buttonBGRemoveColor}
                        onClick={() =>
                          setNewConfirmation(
                            t("general:label_title_remove"),
                            t("general:label_description_remove"),
                            async () => {
                              await handleRemove(addtional!, () => handleGetAddtional(onClonse))
                            },
                          )
                        }
                        descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.button.remove"
                      />
                    </>
                  )}
                </CanView>
              </section>

              {addtional && addtional.id !== -1 && (
                <>
                  <GetSpace top={5} />

                  <section className={defaultStyle.buttonsController}>
                    <CanView permission={Permissions.CanEditeAddtionals}>
                      <>
                        <Button
                          label={t("buttons:button_apply")}
                          typeButton="button"
                          type="primary"
                          size="medium"
                          bgColor={THEME.colors.buttonBGRegisterColor}
                          onClick={() => setEnableApplyPopup(true)}
                          descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.button.apply"
                        />

                        <GetSpace right={5} />

                        <Button
                          label={t("button_remove_apply_automatic")}
                          typeButton="button"
                          type="primary"
                          size="medium"
                          bgColor={THEME.colors.buttonBGCloseColor}
                          onClick={() => setEnableRemoveApplyPopup(true)}
                          descriptionKey="interactionDescriptions:pages.products.addtional.addtionalPupop.button.removeApplyAutomatic"
                        />
                      </>
                    </CanView>
                  </section>
                </>
              )}
            </form>

            {isRegisterEnable && addtional && (
              <ViewRegister id={addtional.id} typeEntity="AdditionalEntityId" onClose={handleDisableRegister} />
            )}

            {showAssociations && addtional && (
              <AssociationsPopup
                title={t("general:label_related_title", { name: addtional.name })}
                emptyMessage={t("general:label_related_empty")}
                onClose={() => setShowAssociations(false)}
                items={resolveAdditionalAssociations(
                  addtional,
                  mealsMemory.listData,
                  drinksMemory.listData,
                  comboMemory.listData,
                )}
              />
            )}

            {enableApplyPopup && addtional && (
              <AdditionalApplyPopup
                title={t("label_title_apply_automatic")}
                actionLabel={t("buttons:button_apply")}
                helpKey="pages.products.addtional.addtionalpupop.2"
                helpTitle={t("helpPopup:sections.additional.applyAutomatic.title")}
                helpDescription={t("helpPopup:sections.additional.applyAutomatic.description")}
                helpBullets={
                  t("helpPopup:sections.additional.applyAutomatic.bullets", { returnObjects: true }) as string[]
                }
                onClose={() => setEnableApplyPopup(false)}
                onConfirm={async (request) => {
                  await handleApplyAdditional(addtional.id, request, () => {
                    refreshProductAssociations()
                    setEnableApplyPopup(false)
                  })
                }}
              />
            )}

            {enableRemoveApplyPopup && addtional && (
              <AdditionalApplyPopup
                title={t("label_title_remove_apply_automatic")}
                actionLabel={t("button_remove_apply_automatic")}
                helpKey="pages.products.addtional.addtionalpupop.3"
                helpTitle={t("helpPopup:sections.additional.removeAutomatic.title")}
                helpDescription={t("helpPopup:sections.additional.removeAutomatic.description")}
                helpBullets={
                  t("helpPopup:sections.additional.removeAutomatic.bullets", { returnObjects: true }) as string[]
                }
                onClose={() => setEnableRemoveApplyPopup(false)}
                onConfirm={async (request) => {
                  await handleRemoveAdditionalApply(addtional.id, request, () => {
                    refreshProductAssociations()
                    setEnableRemoveApplyPopup(false)
                  })
                }}
              />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
